import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { ApiError, get, post } from "../api/client";
import type { Me, PlanCode } from "../api/types";

interface Auth {
  me: Me | null;
  ready: boolean;
  plan: PlanCode;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const Ctx = createContext<Auth | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [ready, setReady] = useState(false);
  const refresh = useCallback(async () => {
    try {
      setMe(await get<Me>("/v1/me"));
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) setMe(null);
      else throw e;
    } finally {
      setReady(true);
    }
  }, []);
  useEffect(() => {
    void refresh();
  }, [refresh]);
  const signOut = useCallback(async () => {
    try {
      await post("/v1/auth/logout");
    } finally {
      setMe(null);
    }
  }, []);
  const plan: PlanCode = me?.usage?.plan_code ?? me?.workspaces.find((w) => w.id === me.user.personal_workspace_id)?.plan_code ?? "free";
  const value = useMemo(() => ({ me, ready, plan, refresh, signOut }), [me, ready, plan, refresh, signOut]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): Auth {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth outside AuthProvider");
  return v;
}
